<template>
  <div>
    <app-title-bar>
      <app-material-icon name="account_circle" />
      <h2 class="text-text-primary">{{ $t("profile.views.PersonalInformation.personalInfo") }}</h2>
    </app-title-bar>

    <div class="layout-spacing">
      <AppCard class="pa-6 border" elevation="0">
        <v-form :disabled="updateProfilePending"
                @submit.prevent="submit"
        >
          <v-row>
            <v-col cols="12">
              <i18n-t keypath="profile.views.PersonalInformation.requiredFields"
                      tag="p"
                      class="mb-4 text-text-secondary"
                      scope="global"
              >
                <template #asterisk>
                  <span class="text-text-danger">*</span>
                </template>
              </i18n-t>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <AppTextField v-model.trim="model.firstName"
                            autocomplete="given-name"
                            :label="$t('common.firstName')"
                            required
                            class="required"
                            maxlength="50"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <AppTextField v-model.trim="model.lastName"
                            autocomplete="family-name"
                            :label="$t('profile.views.PersonalInformation.usageLastName')"
                            maxlength="50"
              >
                <template #append-inner>
                  <AppTooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <app-material-icon name="help" v-bind="props" />
                    </template>
                    <span>{{ $t("profile.views.PersonalInformation.usageLastNameExample") }}</span>
                  </AppTooltip>
                </template>
              </AppTextField>
            </v-col>
            <v-col cols="12" sm="6">
              <AppTextField v-model.trim="model.birthName"
                            :label="$t('common.birthName')"
                            maxlength="50"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <AppTextField disabled
                            :model-value="initials"
                            :label="$t('common.initials')"
              >
                <template #append-inner>
                  <AppTooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <app-material-icon name="help" v-bind="props" />
                    </template>
                    <span>{{ $t("profile.views.PersonalInformation.infoUsedForSigning") }}</span>
                  </AppTooltip>
                </template>
              </AppTextField>
            </v-col>
            <v-col cols="12" sm="6">
              <AppTextField disabled
                            :model-value="model.email"
                            :label="$t('common.email')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <AppPhoneField :value="model.telephone"
                            class="w-100"
                            :label="$t('common.phoneNumber')"
                            @on-input="handlePhoneChange"
              >
                <template #append-inner>
                  <AppTooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <app-material-icon name="help" v-bind="props" />
                    </template>
                    <span>{{ $t("profile.views.PersonalInformation.infoForSigningAuth") }}</span>
                  </AppTooltip>
                </template>
              </AppPhoneField>
            </v-col>
            <v-col cols="12" sm="6">
              <AppDatePicker
                v-model="model.birthDate"
                :label="$t('common.birthDate')"
                :clearable="false"
                autocomplete="bday"
                view-mode="year"
              >
                <template #append-inner>
                  <AppTooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <app-material-icon name="help" v-bind="props" />
                    </template>
                    <span>{{ $t("profile.views.PersonalInformation.infoRequiredForIdCheck") }}</span>
                  </AppTooltip>
                </template>
              </AppDatePicker>
            </v-col>

            <v-col cols="12" sm="6">
              <AppSelect v-model="model.gender"
                        :items="genders"
                        autocomplete="sexe"
                        :label="$t('common.gender')"
              >
                <template #append-inner>
                  <AppTooltip location="bottom" max-width="300">
                    <template v-slot:activator="{ props }">
                      <app-material-icon name="help" v-bind="props" />
                    </template>
                    <span>{{ $t("profile.views.PersonalInformation.infoRequiredForIdCheck") }}</span>
                  </AppTooltip>
                </template>
              </AppSelect>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-divider/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <AppTextField v-model.trim="model.company"
                            autocomplete="organization"
                            :label="$t('common.company')"
                            maxlength="100"
              />
            </v-col>

            <v-col v-if="profile.subscription" cols="12" sm="6">
              <AppTextField disabled
                            :model-value="profile.subscription.name"
                            :label="$t('profile.views.PersonalInformation.subscription')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <app-button
                color="primary"
                :disabled="updateProfilePending"
                :loading="updateProfilePending"
                @click="submit"
              >
                {{ $t("profile.views.PersonalInformation.submit") }}
              </app-button>
              <input type="submit" hidden/>
            </v-col>
          </v-row>
        </v-form>
      </AppCard>
    </div>

    <AppDialog :is-open="unsavedChangesModal"
              :ok-text="$t('profile.views.PersonalInformation.unsavedChangesModalConfirm')"
              :cancel-text="$t('profile.views.PersonalInformation.unsavedChangesModalCancel')"
              @cancel="cancelUnsavedChanges"
              @ok="confirmUnsavedChanges"
    >
      <template #title>
        {{$t('profile.views.PersonalInformation.unsavedChangesModalTitle')}}
      </template>
      <template #body>
        {{$t('profile.views.PersonalInformation.unsavedChangesModalBody')}}
      </template>
    </AppDialog>

    <AppDialog :is-open="idCheckChangesModal"
              @cancel="idCheckChangesModal = false"
              @ok="confirmIdCheckChanges"
    >
      <template #title>
        {{$t('profile.views.PersonalInformation.idCheckChangesModalTitle')}}
      </template>
      <template #body>
        {{$t('profile.views.PersonalInformation.idCheckChangesModalBody')}}
      </template>
    </AppDialog>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDatePicker from '@/common/AppDatePicker.vue'
import AppPhoneField from '@/common/AppPhoneField.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { ISOToYYYYMMDD } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import { LOGOUT_ROUTE_NAME } from '@/router/routerData'
import { UPDATE_PROFILE } from '@/store/modules/user/action_types'
import { SET_USER_PROFILE } from '@/store/modules/user/mutation_types'


const { mapState, mapActions, mapMutations } = createNamespacedHelpers('user')
const PROFILE_KEYS = [
  'firstName',
  'lastName',
  'email',
  'birthDate',
  'birthName',
  'gender',
  'company',
  'telephone',
]
const IDCHECK_PROFILE_KEYS = [
  'firstName',
  'lastName',
  'birthDate',
  'birthName',
  'gender',
]
export default {
  name: 'PersonalInformation',
  components: {
    AppDialog,
    AppSelect,
    AppPhoneField,
    AppTextField,
    AppTooltip,
    AppDatePicker,
    AppCard,
  },
  data() {
    return {
      unsavedChangesModal: false,
      idCheckChangesModal: false,
      genders: [
        {
          title: this.$t('common.male'),
          value: 'M',
        },
        {
          title: this.$t('common.female'),
          value: 'F',
        },
      ],
      model: {
        firstName: '',
        lastName: '',
        birthDate: '',
        birthName: '',
        gender: '',
        company: '',
        email: '',
        telephone: '',
      },
      menu: false,
    }
  },
  computed: {
    ...mapGetters('user', ['canCurrentUserSign']),
    ...mapState(['profile', 'updateProfilePending']),
    computedBirthDateValue: {
      get() {
        if (this.model.birthDate) {
          return ISOToYYYYMMDD(this.model.birthDate)
        }
        return undefined
      },
      set(val) {
        this.model.birthDate = val
      },
    },
    initials() {
      const { firstName, lastName } = this.model
      return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`
    },
  },
  created () {
    const {
      firstName,
      lastName,
      email,
      birthDate,
      birthName,
      gender,
      company,
      telephone,
    } = this.profile
    this.model = {
      firstName,
      lastName,
      birthDate,
      email,
      birthName,
      gender,
      company,
      telephone,
    }
  },
  beforeRouteLeave(to, from, next) {
    const isNavigationToLogout = to.name === LOGOUT_ROUTE_NAME
    if (isNavigationToLogout) {
      next()
    }

    const unsavedChanges = this.isModelDifferentFromSource(PROFILE_KEYS)
    if (unsavedChanges) {
      this.unsavedChangesModal = true
      this.unsavedChangesConfirmCallback = next
    } else {
      next()
    }
  },
  methods: {
    ...mapActions([UPDATE_PROFILE]),
    ...mapMutations([SET_USER_PROFILE]),
    handlePhoneChange(input, event) {
      this.model.telephone = event.number || input
    },
    async submit() {
      if (this.canCurrentUserSign && this.isModelDifferentFromSource(IDCHECK_PROFILE_KEYS)) {
        this.idCheckChangesModal = true
      } else {
        this.saveUser()
      }
    },
    async saveUser() {
      try {
        await this.UPDATE_PROFILE({
          ...this.model,
          birthDate: this.computedBirthDateValue,
          initials: this.initials,
        })

        toast.success(this.$t('profile.views.PersonalInformation.confirmNotification'))
      } catch (e) {
        // TODO: Do something custom ?
      }
    },
    save(date) {
      this.$refs.menu.save(date)
    },
    /**
     * Checks if the model is different from the original source by checking the keys parameter of both the model and source
     * @param {array} keys The property keys to check for difference
     * @returns {boolean} True if any property from the keys param is different between the model and source, false otherwise
     */
    isModelDifferentFromSource(keys) {
      for (const key of keys) {
        if (this.model[key] !== this.profile[key]) {
          return true
        }
      }

      return false
    },
    confirmUnsavedChanges() {
      this.saveUser()
      this.unsavedChangesConfirmCallback()
    },
    cancelUnsavedChanges() {
      this.unsavedChangesConfirmCallback()
    },
    async confirmIdCheckChanges() {
      this.idCheckChangesModal = false
      await this.saveUser()
      if (Array.isArray(this.profile.missingInfoToSign)) {
        if (!this.profile.missingInfoToSign.includes('idCheck')) {
          this.profile.missingInfoToSign.push('idCheck')
        }
      } else {
        this.profile.missingInfoToSign = ['idCheck']
      }
      this.SET_USER_PROFILE(this.profile)
    },
  },
}
</script>

<style lang="scss" scoped>
.v-row {
  row-gap: 8px;
}
</style>
